import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { HrmCodeAttendDto } from 'src/dtos/hrmCodeAttend/hrmCodeAttendDto';
import { HrmWorkShiftDto } from 'src/dtos/hrmWorkShift/hrmWorkShiftDto';
import {
    defaultHRM2010_R01DetailDto,
    defaultHRM2010_R01HeaderDto,
    HRM2010_R01DetailDto,
    HRM2010_R01HeaderDto,
} from './Dto_HRM2010_R01';

/** 일일근태 조회 조건이다. */
export interface HRM2010_R01Filter {
    workDt: string; // 출근일 기준 근무일자
    compCd: string; // 사업장
    orgCd: string; // 부서
    attendCd: string; // 근태코드
    outYn: string; // 외출 있음 여부
    shiftCd: string; // 근무조
}

/** 현지 오늘을 서버 날짜 문자열로 만든다. */
const today = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/** 일일근태 조회 초기 조건이다. */
export const defaultHRM2010_R01Filter = (): HRM2010_R01Filter => ({
    workDt: today(),
    compCd: '',
    orgCd: '',
    attendCd: '',
    outYn: '',
    shiftCd: '',
});

/** 서버 행에 그리드 식별값과 순서를 부여한다. */
const buildRows = <T extends { valueIndex: number; orderIndex: number; orderIndexSeq: number }>(
    rows: T[],
    defaults: T,
): T[] => rows.map((row, index) => ({
    ...defaults,
    ...row,
    valueIndex: index + 1,
    orderIndex: index + 1,
    orderIndexSeq: 0,
}));

/** 일일근태 목록·외출 기록·그리드 상태를 보관한다. */
export interface Store_HRM2010_R01 {
    HRM2010_R01Filter: HRM2010_R01Filter; // 현재 조회 조건
    HRM2010_R01PastFilter: HRM2010_R01Filter; // 마지막 조회 조건
    setHRM2010_R01Filter: (value: Partial<HRM2010_R01Filter>) => void; // 현재 조건 갱신
    setHRM2010_R01PastFilter: (value: HRM2010_R01Filter) => void; // 마지막 조건 갱신
    HRM2010_R01List: HRM2010_R01HeaderDto[]; // 위 그리드 목록
    originalHRM2010_R01List: HRM2010_R01HeaderDto[]; // 조회 직후 위 목록
    setHRM2010_R01List: (value: HRM2010_R01HeaderDto[]) => void; // 위 목록 교체
    setOriginalHRM2010_R01List: (value: HRM2010_R01HeaderDto[]) => void; // 위 원본 교체
    updateHRM2010_R01List: (index: number, value: HRM2010_R01HeaderDto) => void; // 위 행 갱신
    selectedHRM2010_R01?: HRM2010_R01HeaderDto; // 선택한 위 행
    setSelectedHRM2010_R01: (value?: HRM2010_R01HeaderDto) => void; // 위 행 선택
    deletedHRM2010_R01List: string[]; // 삭제 표시한 위 행 ID
    setDeletedHRM2010_R01List: (value: string[]) => void; // 위 삭제 표시 변경
    HRM2010_R01DetailList: HRM2010_R01DetailDto[]; // 아래 그리드 기록
    originalHRM2010_R01DetailList: HRM2010_R01DetailDto[]; // 조회 직후 아래 기록
    setHRM2010_R01DetailList: (value: HRM2010_R01DetailDto[]) => void; // 아래 기록 교체
    setOriginalHRM2010_R01DetailList: (value: HRM2010_R01DetailDto[]) => void; // 아래 원본 교체
    updateHRM2010_R01DetailList: (index: number, value: HRM2010_R01DetailDto) => void; // 아래 행 갱신
    insertHRM2010_R01DetailList: (value: HRM2010_R01DetailDto, afterValueIndex?: number) => void; // 아래 행 추가
    removeHRM2010_R01DetailList: (index: number) => void; // 아래 행 제거
    selectedHRM2010_R01DetailList: string[]; // 아래 선택 행 ID
    setSelectedHRM2010_R01DetailList: (value: string[]) => void; // 아래 선택 변경
    deletedHRM2010_R01DetailList: string[]; // 아래 삭제 표시 ID
    setDeletedHRM2010_R01DetailList: (value: string[]) => void; // 아래 삭제 변경
    activeGridType: 'header' | 'detail'; // 액션바 삭제 대상
    setActiveGridType: (value: 'header' | 'detail') => void; // 활성 그리드 변경
    workShiftList: HrmWorkShiftDto[]; // 사용 중인 근무조
    setWorkShiftList: (value: HrmWorkShiftDto[]) => void; // 근무조 콤보 갱신
    attendCodeList: HrmCodeAttendDto[]; // 사용 중인 근태코드
    setAttendCodeList: (value: HrmCodeAttendDto[]) => void; // 근태 콤보 갱신
    columnState: ColumnState[] | null; // 위 컬럼 배치
    detailColumnState: ColumnState[] | null; // 아래 컬럼 배치
    setColumnState: (value: ColumnState[] | null) => void; // 위 컬럼 배치 변경
    setDetailColumnState: (value: ColumnState[] | null) => void; // 아래 컬럼 배치 변경
    scrollTop: number | null; // 위 스크롤 위치
    detailScrollTop: number | null; // 아래 스크롤 위치
    setScrollTop: (value: number | null) => void; // 위 스크롤 변경
    setDetailScrollTop: (value: number | null) => void; // 아래 스크롤 변경
    initData: () => void; // 화면 상태 초기화
}

/** 일일근태 화면의 상태와 순서 보존 작업을 제공한다. */
export const useHRM2010_R01Store = create<Store_HRM2010_R01>((set) => ({
    HRM2010_R01Filter: defaultHRM2010_R01Filter(),
    HRM2010_R01PastFilter: defaultHRM2010_R01Filter(),
    setHRM2010_R01Filter: value => set(state => ({
        HRM2010_R01Filter: { ...state.HRM2010_R01Filter, ...value },
    })),
    setHRM2010_R01PastFilter: value => set({ HRM2010_R01PastFilter: { ...value } }),
    HRM2010_R01List: [],
    originalHRM2010_R01List: [],
    setHRM2010_R01List: value => set({
        HRM2010_R01List: buildRows(value, defaultHRM2010_R01HeaderDto),
        deletedHRM2010_R01List: [],
    }),
    setOriginalHRM2010_R01List: value => set({
        originalHRM2010_R01List: buildRows(value, defaultHRM2010_R01HeaderDto),
    }),
    updateHRM2010_R01List: (index, value) => set(state => ({
        HRM2010_R01List: state.HRM2010_R01List.map(row => (
            row.valueIndex === index ? value : row
        )),
        selectedHRM2010_R01: state.selectedHRM2010_R01?.valueIndex === index
            ? value
            : state.selectedHRM2010_R01,
    })),
    selectedHRM2010_R01: undefined,
    setSelectedHRM2010_R01: value => set({ selectedHRM2010_R01: value }),
    deletedHRM2010_R01List: [],
    setDeletedHRM2010_R01List: value => set({ deletedHRM2010_R01List: value.map(String) }),
    HRM2010_R01DetailList: [],
    originalHRM2010_R01DetailList: [],
    setHRM2010_R01DetailList: value => set({
        HRM2010_R01DetailList: buildRows(value, defaultHRM2010_R01DetailDto),
        selectedHRM2010_R01DetailList: [],
        deletedHRM2010_R01DetailList: [],
    }),
    setOriginalHRM2010_R01DetailList: value => set({
        originalHRM2010_R01DetailList: buildRows(value, defaultHRM2010_R01DetailDto),
    }),
    updateHRM2010_R01DetailList: (index, value) => set(state => ({
        HRM2010_R01DetailList: state.HRM2010_R01DetailList.map(row => (
            row.valueIndex === index ? value : row
        )),
    })),
    insertHRM2010_R01DetailList: (value, afterValueIndex) => set(state => {
        const rows = [...state.HRM2010_R01DetailList];
        const target = rows.findIndex(row => row.valueIndex === afterValueIndex);
        const position = afterValueIndex === undefined || target < 0 ? rows.length : target + 1;
        const nextIndex = Math.max(0, ...rows.map(row => row.valueIndex)) + 1;
        rows.splice(position, 0, { ...value, valueIndex: nextIndex });
        return { HRM2010_R01DetailList: rows };
    }),
    removeHRM2010_R01DetailList: index => set(state => ({
        HRM2010_R01DetailList: state.HRM2010_R01DetailList.filter(row => row.valueIndex !== index),
    })),
    selectedHRM2010_R01DetailList: [],
    setSelectedHRM2010_R01DetailList: value => set({ selectedHRM2010_R01DetailList: value.map(String) }),
    deletedHRM2010_R01DetailList: [],
    setDeletedHRM2010_R01DetailList: value => set({ deletedHRM2010_R01DetailList: value.map(String) }),
    activeGridType: 'header',
    setActiveGridType: value => set({ activeGridType: value }),
    workShiftList: [],
    setWorkShiftList: value => set({ workShiftList: value }),
    attendCodeList: [],
    setAttendCodeList: value => set({ attendCodeList: value }),
    columnState: null,
    detailColumnState: null,
    setColumnState: value => set({ columnState: value }),
    setDetailColumnState: value => set({ detailColumnState: value }),
    scrollTop: null,
    detailScrollTop: null,
    setScrollTop: value => set({ scrollTop: value }),
    setDetailScrollTop: value => set({ detailScrollTop: value }),
    initData: () => set({
        HRM2010_R01Filter: defaultHRM2010_R01Filter(),
        HRM2010_R01PastFilter: defaultHRM2010_R01Filter(),
        HRM2010_R01List: [],
        originalHRM2010_R01List: [],
        selectedHRM2010_R01: undefined,
        deletedHRM2010_R01List: [],
        HRM2010_R01DetailList: [],
        originalHRM2010_R01DetailList: [],
        selectedHRM2010_R01DetailList: [],
        deletedHRM2010_R01DetailList: [],
        activeGridType: 'header',
        workShiftList: [],
        attendCodeList: [],
        columnState: null,
        detailColumnState: null,
        scrollTop: null,
        detailScrollTop: null,
    }),
}));
